import dayjs from "dayjs";
import isBetween from 'dayjs/plugin/isBetween';
import { Box, Button, LinearProgress } from "@mui/material";
import { Close, Download } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import { forwardRef, useEffect, useRef, useState } from "react";
import { checkFile, databaseDateToFilename, saveFile, trackEvent } from "./Utilities";
import { Zip, ZipPassThrough } from 'fflate';
import { useSelector, useDispatch } from "react-redux";
import { getData, setDownload, setGeneration } from "../api/dataSlice";
import { useKeycloak } from "./Keycloak";
import { useAddTimelapseJobMutation, useCancelTimelapseGenerationMutation } from "../api/graphqlApi";
import { useSubscription } from "@apollo/client";
import { timelapseJobStatusUpdatedByUuid } from "../api/graphqlSubscription";

dayjs.extend(isBetween)

// see: https://github.com/101arrowz/fflate/wiki/Guide:-Modern-(Buildless) 
// see: https://github.com/101arrowz/fflate/discussions/92
const fflToRS = fflateStream => (
    new ReadableStream({
        start(controller) {
            fflateStream.ondata = (err, data, final) => {
                if (err) {
                    controller.error(err)
                } else {
                    controller.enqueue(data)
                    if (final) {
                        controller.close()
                    }
                }
            }
        },
        cancel() {
            fflateStream.terminate()
        }
    })
)

const addFileToZip = async(fileData, fileName, zip) => {
    const zippedFileStream = new ZipPassThrough(fileName)
    zip.add(zippedFileStream)
    const fileReader = fileData.stream().getReader()
    while (true) {
        const { done, value } = await fileReader.read()
        if (done) {
            zippedFileStream.push(new Uint8Array(0), true)
            break
        }
        zippedFileStream.push(value)
    }
}

export default forwardRef(function DownloadButton(props, ref) {
    
    const {
        targetFilename,
        sourceUrl, // total download only
        fileType, // total download only
        selectedCamera, // custom download only
        dateRange, // custom download only
        disabled: disabledByParent,
        setAlertMessage,
        selectedResolution, // custom timelapse only
        maxLength, // custom timelapse only
        ...rest // pass down all other props so that the Tooltip component works
    } = props;

    const [addTimelapseJob, { data: dataAddTimelapse, isLoading: loadingAddTimelapse, error: errorAddTimelapse }] = useAddTimelapseJobMutation()
    const [cancelTimelapseGeneration, { isLoading: loadingCancelTimelapse, error: errorCancelTimelapse }] = useCancelTimelapseGenerationMutation()
    const [disabled, setDisabled] = useState(disabledByParent || true)
    const [isCanceling, setIsCanceling] = useState(false)
    
    const { t, i18n } = useTranslation()
    const { source, download, generation } = useSelector(getData)
    const { DEBUG, CAMERAS } = window.conf
    const { getUsername, getEmail } = useKeycloak()
    const { data: dataTimelapseStatus, loading: loadingTimelapseStatus, error: errorTimelapseStatus } = useSubscription(timelapseJobStatusUpdatedByUuid, { variables: { uUID: dataAddTimelapse?.uUID }, skip: !dataAddTimelapse?.uUID })

    const dispatch = useDispatch()
    const abortDownloadSingle = useRef()
    const abortDownloadMultiple = useRef()
    const totalTimelapseGenerationDelay = useRef()
    const isTotalDownload = sourceUrl && !(selectedCamera || selectedCamera === 0) && !dateRange && !selectedResolution && !maxLength
    const isCustomImages = !sourceUrl && (selectedCamera || selectedCamera === 0) && dateRange && !selectedResolution && !maxLength
    const isCustomTimelapse = !sourceUrl && (selectedCamera || selectedCamera === 0) && dateRange && selectedResolution && (maxLength || maxLength === 0)
    const loadingApi = loadingAddTimelapse || loadingCancelTimelapse || loadingTimelapseStatus
    const loadingStates = download?.loading || generation?.loading

    // control disable state, check if file at url exists and is of the correct filetype for downloading single file from url (total download only)
    useEffect(() => {
        if (disabledByParent) {
            if (!disabled) setDisabled(true)
            return
        }

        if (!sourceUrl) {
            if (disabled) setDisabled(false)
            return
        }

        let cancelled = false
        const checkFileAsync = async() => {
            const { exists, contentType } = await checkFile(sourceUrl)
            if (cancelled) return
            const success = exists && contentType === fileType
            setDisabled(!success)
            if (!success) setAlertMessage && setAlertMessage({ type: "error", message: t("msg.no_file") })
        }

        checkFileAsync()
        return () => { cancelled = true }

    }, [sourceUrl, disabledByParent]) // eslint-disable-line react-hooks/exhaustive-deps

    // display last error that occurred from mutations or subscriptions in alert message
    useEffect(() => {
        const errors = [errorAddTimelapse, errorCancelTimelapse, errorTimelapseStatus].filter(Boolean)
        if (errors.length > 0) {
            resetDownloadState()
            resetGenerationState()
            const latestError = errors[errors.length - 1]
            setAlertMessage && setAlertMessage({
                type: "error",
                message: latestError === errorTimelapseStatus 
                    ? latestError.message 
                    : latestError.message.split(".")[0] + "."
            })
            console.error("[DownloadButton]", errors)
        }
    }, [errorAddTimelapse, errorCancelTimelapse, errorTimelapseStatus]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (dataTimelapseStatus) {
            const res = dataTimelapseStatus.timelapseJobStatusUpdatedByUuid
            const jobParameter = res?.jobParameter
    
            DEBUG && console.log("[DownloadButton] Timelapse job status changed:", res)
    
            switch (jobParameter?.state) {
                case "finished":
                    downloadSingle(res.timelapseUrl)
                    resetGenerationState()
                    break
                case "failed":
                    resetGenerationState()
                    setAlertMessage && setAlertMessage({ type: "error", message: t("timelapse.error.job_failed") })
                    break
                case "cancelled":
                    abortDownloads()
                    resetDownloadState()
                    resetGenerationState()
                    resetAlertMessage()
                    setIsCanceling(false)
                    break
                case "processing":
                    setAlertMessage && setAlertMessage({ type: "info", message: t("timelapse.receive_email") })

                    // keep total delay if already set otherwise set new
                    if (!totalTimelapseGenerationDelay.current) {
                        totalTimelapseGenerationDelay.current = jobParameter.delay || 0
                    }

                    const percentComplete = (((totalTimelapseGenerationDelay.current - (jobParameter.delay || 0)) * 100) / totalTimelapseGenerationDelay.current) || 0

                    DEBUG && console.log("[DownloadButton] Timelapse generation progress:", percentComplete)

                    dispatch(setGeneration({ loading: true, progress: percentComplete }))
                    
                    break
                default:
                    break
            }
        }
    }, [dataTimelapseStatus]) // eslint-disable-line react-hooks/exhaustive-deps

    const onClickDownload = () => {
        if (isTotalDownload) {
            downloadSingle(sourceUrl)
        } else if (isCustomTimelapse) {
            createAndDownloadCustomTimelapse()
        } else if (isCustomImages) {
            downloadMultiple()
        }
    }

    const onClickCancel = () => {
        if (isCustomTimelapse && generation?.loading) {
            cancelTimelapseGeneration({ uUID: dataAddTimelapse?.uUID })
            setIsCanceling(true) // helper state to ensure Cancel button is disabled while canceling cause mutation is too slow
        } else {
            abortDownloads()
            resetDownloadState()
            resetAlertMessage()
        }
    }

    function abortDownloads() {
        if (abortDownloadSingle.current) abortDownloadSingle.current.abort()
        if (abortDownloadMultiple.current) abortDownloadMultiple.current.abort()
    }

    function resetDownloadState() {
        dispatch(setDownload({ loading: false, progress: 0 }))
    }

    function resetGenerationState() {
        dispatch(setGeneration({ loading: false, progress: 0 }))
    }

    function resetAlertMessage() {
        setAlertMessage && setAlertMessage(null)
    }

    function createAndDownloadCustomTimelapse() {
        dispatch(setGeneration({ loading: true, progress: 0 }))
        trackEvent(`${t("pages.timelapse", { lng: "de" })} ${t("download.custom", { lng: "de" })}`, t("actions.download", { lng: "de" }))
        const data = {
            featureDetailId: CAMERAS[selectedCamera].featureDetailId,
            timespanStart: dateRange[0].format("YYYY-MM-DD") + "T00:00:00.000Z",
            timespanEnd: dateRange[1].format("YYYY-MM-DD") + "T23:59:59.999Z",
            width: selectedResolution.width,
            height: selectedResolution.height,
            maxLength,
            userName: getUsername() || "",
            userMail: getEmail() || "",
            language: i18n.language
        }
        DEBUG && console.log("[DownloadButton] Generate timelapse with parameters:", data)
        addTimelapseJob(data)
    }

    function downloadSingle(url = sourceUrl) {

        DEBUG && console.log("[DownloadButton] Download from url:", url)

        dispatch(setDownload({ loading: true, progress: 0 }))
        
        url = url.replace(/^\/+|\/+$/g, '') // remove leading and trailing slashes
        const xhr = new XMLHttpRequest()
        abortDownloadSingle.current = xhr // set created xhr object to ref to be able to cancel later

        xhr.open("GET", url, true)
        xhr.responseType = "blob"

        const progressBuffer = []; // array for the moving average
        const maxBufferSize = 5; // how many steps are to be averaged

        xhr.onprogress = (event) => {
            if (event.lengthComputable) {
                const percentComplete = (event.loaded / event.total) * 100;

                // add to the buffer and shorten if necessary
                progressBuffer.push(percentComplete);
                if (progressBuffer.length > maxBufferSize) {
                    progressBuffer.shift(); // remove oldest entries
                }

                // calculate average
                const averageProgress = progressBuffer.reduce((a, b) => a + b, 0) / progressBuffer.length;

                DEBUG && console.log("[DownloadButton] Download progress:", averageProgress)

                dispatch(setDownload({ loading: true, progress: averageProgress }))
            }
        }

        xhr.onload = () => {
            if (xhr.status === 200) {
                saveFile(xhr.response, targetFilename)
                resetDownloadState()
                resetAlertMessage()
            }
        }

        xhr.onerror = (event) => {
            console.error("[DownloadButton] Download single file failed:", event)
            resetDownloadState()
            setAlertMessage && setAlertMessage({ type: "error", message: t("msg.download_failed") })
        }

        xhr.send()
    }

    function downloadMultiple() {

        if (abortDownloadMultiple.current) abortDownloadMultiple.current.abort();
        abortDownloadMultiple.current = new AbortController();
        const { signal } = abortDownloadMultiple.current;

        dispatch(setDownload({ loading: true, progress: 0 }))

        const filteredData = []
        const list = source?.[0]

        for (let i=0; i<list?.length; i++) {

            const elem = list[i]
            const databaseDateByCamera = elem[1]?.[selectedCamera]?.datum

            // compare selected camera image date with span to before pushing into download list
            if (dayjs(elem?.[0]).isBetween(dateRange[0], dateRange[1], "day", "[]")
                && dayjs(databaseDateByCamera).isBetween(dateRange[0], dateRange[1], "day", "[]")) { 
                filteredData.push(databaseDateToFilename(databaseDateByCamera))
            }            
        }

        if (filteredData.length) {
            downloadAndZip(0, [...new Set(filteredData)], signal)
        } else {
            dispatch(setDownload({ loading: false, progress: 0 }))
            setAlertMessage && setAlertMessage({ type: "error", message: t("msg.no_imgs") })
        }
    }

    function downloadAndZip(index, data, signal, zip = new Zip(), zipReadableStream = fflToRS(zip)) {

        // if all images have been downloaded, finish the zip
        if (index >= data.length) {
            zip.end()
            new Response(zipReadableStream).blob().then(res => saveFile(res, `${targetFilename}.zip`))
            resetDownloadState()
            return
        }

        const currentFile = data[index]

        fetch(CAMERAS[selectedCamera]?.download + currentFile, { signal })
        .then(res => {
            if (!res.ok || !res.headers.get('Content-Type').includes("image")) {
                DEBUG && console.warn(`[DownloadButton] Skipping not found image: ${currentFile}`)
                dispatch(setDownload({ loading: true, progress: ((index) * 100) / data.length }))
                downloadAndZip(index + 1, data, signal, zip, zipReadableStream) // try next image
                return
            }

            return res.blob()
        })  
        .then(blob => {
            if (blob) {
                // add the image to the zip (return-statement ensures that the function is terminated)
                return addFileToZip(blob, currentFile, zip).then(() => {
                    dispatch(setDownload({ loading: true, progress: ((index * 100) / data.length) }))
                    downloadAndZip(index + 1, data, signal, zip, zipReadableStream) // continue with next image
                })
            }
        })
        .catch(error => {
            if (error.name !== "AbortError") {
                console.error("[DownloadButton] Downloading one of multiple files failed:", error)
                resetDownloadState()
                setAlertMessage && setAlertMessage({ type: "error", message: t("msg.download_failed") })
            }
        })
    }

    return (
        <Box 
            sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "8px"
            }}
            ref={ref}
            {...rest} // pass down all other props so that the Tooltip component works
        >
            
            <Box sx={{ position: "relative", width: "100%" }}>
                <LoadingButton
                    sx={{ width: "100%", transition: "all 0.3s ease-in-out" }}
                    variant="contained"
                    size="large"
                    startIcon={<Download />}
                    onClick={onClickDownload}
                    disabled={disabled}
                    loading={loadingApi || loadingStates}
                    loadingIndicator={`${generation?.loading ? t("timelapse.loading") : t("actions.loading")}...`}
                >
                    {t("actions.download")}
                </LoadingButton>

                {loadingStates && (
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            width: "100%",
                            borderBottomLeftRadius: "4px",
                            borderBottomRightRadius: "4px",
                            overflow: "hidden"
                        }}
                    >
                        <LinearProgress 
                            variant="determinate"
                            value={
                                isCustomTimelapse
                                ? generation?.loading
                                    ? generation?.progress / 2 || 0
                                    : (download?.progress / 2 || 0) + 50 // start from 50%
                                : download?.progress || 0
                            }
                        />
                    </Box>
                )}

            </Box>

            {loadingStates && (
                <Button
                    sx={{ flexShrink: 0 }}
                    variant="outlined"
                    size="large"
                    onClick={onClickCancel}
                    disabled={loadingCancelTimelapse || isCanceling}
                >
                    <Close />
                </Button>
            )}

        </Box>
    )
})